import React, { useState } from 'react';
import AppBar from '@material-ui/core/AppBar/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import Link from '@material-ui/core/Link';
import BokunLogo from 'assets/bokun-logo-simple-white.svg';
import { Link as RouterLink } from 'react-router-dom';
import Drawer from '@material-ui/core/Drawer';
import Hidden from '@material-ui/core/Hidden';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import MenuItem from '@material-ui/core/MenuItem';
import Menu from '@material-ui/core/Menu';
import Chip from '@material-ui/core/Chip';
import IconButton from '@material-ui/core/IconButton';
import { useUser } from 'utils/user';
import MenuIcon from '@material-ui/icons/Menu';
import Avatar from '@material-ui/core/Avatar';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import BusinessIcon from '@material-ui/icons/Business';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useStyles } from './css';
import { useLogout } from '../../hooks/useLogout';
import { SidebarMenu } from '../../components/SidebarMenu/SidebarMenu';
import { useOrganizationStatus } from '../../queries/useOrganizationStatus';
import styled from 'styled-components';
import { Button } from '@material-ui/core';
import { DisguiseDialog } from './DisguiseDialog/DisguiseDialog';
import { useModalContext } from '../../context/ModalContext';
import { useAppUserAccountQuery } from '../../types/generated';

const SC = {
    OrganizationText: styled.div`
        font-size: 14px;
        font-weight: bold;
    `,
    DisguiseMenuItem: styled(MenuItem)`
        @media screen and (min-width: 768px) {
            display: none;
        }
    `,
    DisguiseButton: styled(Button)`
        display: none;
        @media screen and (min-width: 768px) {
            display: flex;
        }
    `,
    LoadingContainer: styled.div`
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
    `,
};

export function ExtranetPages({ children }) {
    // Prefetch organization status
    useOrganizationStatus();
    const classes = useStyles();
    const user = useUser();

    const { data, loading } = useAppUserAccountQuery({ variables: { id: user?.id } });

    const [state, setState] = useState({ userMenuAnchorEl: null });
    const userMenuOpen = Boolean(state.userMenuAnchorEl);
    const handleMenuClose = () => {
        setState({ userMenuAnchorEl: null });
    };

    const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
    const handleDrawerToggle = () => {
        setMobileMenuOpen(!mobileMenuOpen);
    };

    const [logout, { loading: logoutLoading }] = useLogout(() => handleMenuClose());
    const { setVisible } = useModalContext();

    if (loading) {
        return (
            <SC.LoadingContainer>
                <CircularProgress size={80} />
            </SC.LoadingContainer>
        );
    }

    return (
        <div className={classes.root}>
            {data?.appAccountUser?.adminUser && (
                <DisguiseDialog
                    user={data?.appAccountUser}
                    organizationId={data?.appAccountUser?.organizationId}
                />
            )}
            <AppBar position="fixed" className={classes.appBar} elevation={0}>
                <Toolbar>
                    <IconButton
                        color="inherit"
                        edge="start"
                        onClick={handleDrawerToggle}
                        className={classes.menuButton}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Typography variant="h6" className={classes.title}>
                        <Link component={RouterLink} to="/extranet" className={classes.titleLink}>
                            <img src={BokunLogo} className="AppLogo" alt={'Bokun'} />
                        </Link>
                        <span className={classes.appTitle}>Partners</span>
                        <Chip size="small" color="secondary" label="beta" />
                    </Typography>

                    {data?.appAccountUser?.adminUser && (
                        <SC.DisguiseButton color="inherit" onClick={() => setVisible(true)}>
                            <SC.OrganizationText color="inherit">
                                {`(${data?.appAccountUser?.organizationId}) ${user.orgName}`}
                            </SC.OrganizationText>
                        </SC.DisguiseButton>
                    )}

                    <div>
                        <IconButton
                            color="inherit"
                            onClick={event => setState({ userMenuAnchorEl: event.currentTarget })}
                        >
                            <Avatar className={classes.avatar}>
                                {user.firstName.substring(0, 1)}
                                {user.lastName.substring(0, 1)}
                            </Avatar>
                        </IconButton>
                        <Menu
                            anchorEl={state.userMenuAnchorEl}
                            getContentAnchorEl={null}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            keepMounted
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            open={userMenuOpen}
                            onClose={handleMenuClose}
                        >
                            <ListItem disabled>
                                <ListItemIcon>
                                    <AccountCircleIcon />
                                </ListItemIcon>
                                <ListItemText>
                                    {user.firstName} {user.lastName}
                                </ListItemText>
                            </ListItem>
                            {user.orgName && (
                                <ListItem disabled>
                                    <ListItemIcon>
                                        <BusinessIcon />
                                    </ListItemIcon>
                                    <ListItemText>{user.orgName}</ListItemText>
                                </ListItem>
                            )}
                            {data?.appAccountUser?.adminUser && (
                                <SC.DisguiseMenuItem
                                    onClick={() => {
                                        handleMenuClose();
                                        setVisible(true);
                                    }}
                                >
                                    Disguise As Organization
                                </SC.DisguiseMenuItem>
                            )}
                            <MenuItem
                                component={RouterLink}
                                to="/extranet/userprofile"
                                onClick={handleMenuClose}
                            >
                                My Profile
                            </MenuItem>
                            <MenuItem onClick={() => logout()}>
                                {logoutLoading ? (
                                    <CircularProgress size={20} />
                                ) : (
                                    <span>Log out</span>
                                )}
                            </MenuItem>
                        </Menu>
                    </div>
                </Toolbar>
            </AppBar>

            <Hidden mdUp implementation="css">
                <Drawer
                    className={classes.drawer}
                    variant="temporary"
                    open={mobileMenuOpen}
                    onClose={handleDrawerToggle}
                    classes={{
                        paper: classes.drawerPaper,
                    }}
                    ModalProps={{
                        keepMounted: true, // Better open performance on mobile.
                    }}
                >
                    <SidebarMenu callback={() => setMobileMenuOpen(false)} />
                </Drawer>
            </Hidden>
            <Hidden smDown implementation="css">
                <Drawer
                    classes={{
                        paper: classes.drawerPaper,
                    }}
                    variant="permanent"
                    open
                >
                    <div className={classes.toolbar} />
                    <SidebarMenu callback={() => setMobileMenuOpen(false)} />
                </Drawer>
            </Hidden>

            <main className={classes.content}>
                <div className={classes.toolbar} />
                {children}
            </main>
        </div>
    );
}
